// Layer 1 orchestrator. No language model is involved anywhere below this line.
//
// Three guarantees are enforced here rather than trusted:
//   1. Every deterministic flag carries a Receipt.
//   2. Every quote is a verbatim substring of doc.rawText.
//   3. One flag per failure mode per section.
//
// A check that throws is caught, reported by name, and does not stop the others.

import type {
  CheckFailure,
  CheckSkip,
  CompletenessGap,
  DeterministicResult,
  Flag,
  SourceDocument,
} from '../../types';
import { citationCheck, stubResolver, type UrlResolver } from './citationCheck';
import { completenessCheck } from './completenessCheck';
import { generationCheck } from './generationCheck';
import { sourceAttributionCheck } from './sourceAttributionCheck';
import {
  reconcile,
  CHECK_ID as RECONCILE_CHECK_ID,
  type ReconcileOutcome,
  type ReconcileSelection,
} from './reconciliation';

/**
 * One check, finished.
 *
 * Emitted as it happens so that findings can reach the reviewer while the run
 * is still going, rather than all at once when it ends. `flags` has already
 * been through the same guards the finished result applies, so nothing handed
 * out here is anything the final `flags` would not contain.
 */
export interface CheckEvent {
  checkId: string;
  outcome: 'ran' | 'skipped' | 'failed';
  /** What this check produced. Empty on 'skipped' and 'failed'. */
  flags: Flag[];
  /** Set on 'skipped'. */
  skip?: CheckSkip;
  /** Set on 'failed'. Written copy, never an exception message. */
  failure?: CheckFailure;
}

export interface RunOptions {
  /** User-supplied award date, YYYY-MM-DD. Nothing is inferred without it. */
  awardDate?: string;
  /** Injected so the Worker-backed HEAD check can replace the stub. */
  resolveUrl?: UrlResolver;
  /**
   * Table reconciliation runs only on a selection the user has confirmed.
   * Without one the check is skipped and reported as skipped — it never
   * guesses, and it never blocks the other checks.
   */
  reconcileSelection?: ReconcileSelection;
  /**
   * Called as each check settles. The run is otherwise unchanged: a caller
   * that does not pass this gets exactly the result it always got.
   */
  onCheck?: (event: CheckEvent) => void;
}

/** Quotes must be findable in the document exactly as printed. */
function quoteIsVerbatim(doc: SourceDocument, quote: string | null): boolean {
  if (quote === null) return true; // an absence has nothing to quote
  if (quote.trim().length === 0) return false;
  return doc.rawText.includes(quote);
}

export interface Validation {
  kept: Flag[];
  dropped: { flag: Flag; reason: string }[];
}

/**
 * The three guarantees, applied one batch at a time.
 *
 * A run streams: each check's findings reach the reviewer as that check
 * finishes rather than at the end. That is only safe if the guards are applied
 * before a finding is handed out, so the duplicate set is held here and carried
 * across batches instead of being rebuilt over the whole collection at the end.
 * The result is identical to validating everything at once in check order,
 * which is the order the checks are collected in.
 */
export function createValidator(doc: SourceDocument) {
  const seen = new Set<string>();

  return function accept(flags: Flag[]): Validation {
    const kept: Flag[] = [];
    const dropped: { flag: Flag; reason: string }[] = [];

    for (const flag of flags) {
      if (flag.layer === 'deterministic' && !flag.receipt) {
        dropped.push({ flag, reason: 'deterministic flag with no receipt' });
        continue;
      }
      if (!quoteIsVerbatim(doc, flag.quote)) {
        dropped.push({ flag, reason: 'quote is not a verbatim substring of the document' });
        continue;
      }
      const key = `${flag.failureModeId}::${flag.sectionRef}`;
      if (seen.has(key)) {
        dropped.push({ flag, reason: `duplicate ${flag.failureModeId} in ${flag.sectionRef}` });
        continue;
      }
      seen.add(key);
      kept.push(flag);
    }

    return { kept, dropped };
  };
}

export function validateFlags(doc: SourceDocument, flags: Flag[]): Validation {
  return createValidator(doc)(flags);
}

const ORDER = [
  RECONCILE_CHECK_ID,
  'citation-check',
  'source-attribution-check',
  'generation-check',
];

/**
 * Tier 1 first, then by check order, then by page.
 *
 * Exported because a streaming run holds a growing collection and has to put it
 * in the same order the finished run would, or a finding would move in the list
 * once the run completed.
 */
export function sortFlags(flags: Flag[]): Flag[] {
  return [...flags].sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    const ai = ORDER.indexOf(a.checkId);
    const bi = ORDER.indexOf(b.checkId);
    if (ai !== bi) return ai - bi;
    return (a.page ?? 0) - (b.page ?? 0);
  });
}

export async function runDeterministic(
  doc: SourceDocument,
  options: RunOptions = {},
): Promise<
  DeterministicResult & {
    dropped: { flag: Flag; reason: string }[];
    reconciliation: ReconcileOutcome | null;
  }
> {
  const failures: CheckFailure[] = [];
  const ranChecks: string[] = [];
  const skipped: CheckSkip[] = [];
  const collected: Flag[] = [];
  const dropped: { flag: Flag; reason: string }[] = [];
  let gaps: CompletenessGap[] = [];
  let reconciliation: ReconcileOutcome | null = null;

  const accept = createValidator(doc);
  const announce = options.onCheck;

  /** A check that produced findings: guarded, collected, announced. */
  const ran = (checkId: string, flags: Flag[]) => {
    const validated = accept(flags);
    collected.push(...validated.kept);
    dropped.push(...validated.dropped);
    ranChecks.push(checkId);
    announce?.({ checkId, outcome: 'ran', flags: validated.kept });
  };

  /** A check that did not run, and what it was looking for when it gave up. */
  const skip = (checkId: string, reason: string, lookedFor?: string) => {
    const entry: CheckSkip = { checkId, reason, ...(lookedFor ? { lookedFor } : {}) };
    skipped.push(entry);
    announce?.({ checkId, outcome: 'skipped', flags: [], skip: entry });
  };

  // The exception goes to the console; the recorded message is written copy, so
  // that anything which renders a CheckFailure cannot leak a stack message.
  const record = (checkId: string, error: unknown) => {
    console.error(`[${checkId}] check failed`, error);
    const failure: CheckFailure = {
      checkId,
      message:
        'This check stopped before it finished, so nothing from it appears in the findings. The other checks were unaffected. The technical detail is in the browser console.',
    };
    failures.push(failure);
    announce?.({ checkId, outcome: 'failed', flags: [], failure });
  };

  // --- FM-15, table reconciliation: only on a confirmed selection -------
  if (options.reconcileSelection) {
    try {
      reconciliation = reconcile(doc, options.reconcileSelection);
      ran(RECONCILE_CHECK_ID, reconciliation.flags);
    } catch (error) {
      record(RECONCILE_CHECK_ID, error);
    }
  } else if (doc.tables.length === 0) {
    skip(
      RECONCILE_CHECK_ID,
      'Ceiling couldn’t find the tables it needs for this check. Nothing read out of this document has the shape of a savings table, so there is nothing to compare and nothing to confirm. That is a statement about the parse, not a verdict on the plan.',
      'A project-level savings table — one row per measure, a column of annual savings and a total — and a detail table for each measure. Tables are reconstructed from the positions of words on the page, so a table that is an image, or one whose columns are drawn rather than spaced, will not be found.',
    );
  } else {
    skip(
      RECONCILE_CHECK_ID,
      'No summary and detail tables have been confirmed. This check reads figures out of reconstructed tables, so it runs only once a reviewer has confirmed which table is which.',
      'A project-level savings table to reconcile the per-measure detail tables against. Confirming which is which is the reviewer’s call, because a misread table would produce a wrong finding.',
    );
  }

  // --- FM-04, citations -------------------------------------------------
  try {
    ran('citation-check', await citationCheck(doc, options.resolveUrl ?? stubResolver));
  } catch (error) {
    record('citation-check', error);
  }

  // --- FM-08, source attribution ----------------------------------------
  try {
    ran('source-attribution-check', sourceAttributionCheck(doc));
  } catch (error) {
    record('source-attribution-check', error);
  }

  // --- FM-02, IDIQ generation -------------------------------------------
  try {
    ran('generation-check', generationCheck(doc, options.awardDate));
  } catch (error) {
    record('generation-check', error);
  }

  // --- §2.2.4 completeness, gaps rather than flags -----------------------
  // The per-ECM half of this check reads numbered sections. A document whose
  // headings could not be read has none, and a check that cannot see the
  // structure it works on must say so rather than report a clean sweep.
  if (doc.sections.length === 1 && doc.sections[0].number === '') {
    skip(
      'completeness-check',
      'Ceiling couldn’t find the numbered sections it needs for this check. Without them there is no way to tell one measure’s write-up from another’s, so nothing was checked against the FEMP §2.2.4 list. This is not a check that passed.',
      'Numbered headings — "3.2.1 Fixture Power" and the like — to divide the document into sections, and one section per energy conservation measure. A plan whose headings are unnumbered, or whose text layer lost them, will look like a single continuous section.',
    );
  } else {
    try {
      gaps = completenessCheck(doc);
      ran('completeness-check', []);
    } catch (error) {
      record('completeness-check', error);
    }
  }

  const sorted = sortFlags(collected);

  return { flags: sorted, gaps, failures, ranChecks, skipped, dropped, reconciliation };
}
